/**
 * Fresh / Veg Counter POS  (Screen ID: 6.16.13)
 * ─────────────────────────────────────────────────────────────────────────
 * Weight-based / multi-unit pricing.
 * Items priced by kg / gram / pcs / dz (dozen) / bunch.
 * Weight input: manual entry or numeric keypad.
 * Loose-item workflow: select item → enter weight → auto-calculate price → add to bill.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import './VegCounterPOS.css';

const VEG_CATEGORIES = {
  Vegetables: [
    {code:'V001',nm:'Tomato',     rate:40,  unit:'kg',  icon:'🍅', gst:0},
    {code:'V002',nm:'Onion',      rate:35,  unit:'kg',  icon:'🧅', gst:0},
    {code:'V003',nm:'Potato',     rate:30,  unit:'kg',  icon:'🥔', gst:0},
    {code:'V004',nm:'Cabbage',    rate:25,  unit:'kg',  icon:'🥬', gst:0},
    {code:'V005',nm:'Carrot',     rate:55,  unit:'kg',  icon:'🥕', gst:0},
    {code:'V006',nm:'Beans',      rate:80,  unit:'kg',  icon:'🫘', gst:0},
    {code:'V007',nm:'Brinjal',    rate:45,  unit:'kg',  icon:'🍆', gst:0},
    {code:'V008',nm:'Capsicum',   rate:75,  unit:'kg',  icon:'🫑', gst:0},
    {code:'V009',nm:'Bitter Gourd',rate:65, unit:'kg',  icon:'🥒', gst:0},
    {code:'V010',nm:'Lady Finger', rate:70, unit:'kg',  icon:'🌿', gst:0},
    {code:'V011',nm:'Spinach',    rate:30,  unit:'bunch',icon:'🥬', gst:0},
    {code:'V012',nm:'Coriander',  rate:15,  unit:'bunch',icon:'🌿', gst:0},
  ],
  Fruits: [
    {code:'F001',nm:'Banana',     rate:55,  unit:'dz',  icon:'🍌', gst:0},
    {code:'F002',nm:'Apple',      rate:180, unit:'kg',  icon:'🍎', gst:0},
    {code:'F003',nm:'Mango',      rate:120, unit:'kg',  icon:'🥭', gst:0},
    {code:'F004',nm:'Orange',     rate:80,  unit:'kg',  icon:'🍊', gst:0},
    {code:'F005',nm:'Grapes',     rate:90,  unit:'kg',  icon:'🍇', gst:0},
    {code:'F006',nm:'Watermelon', rate:25,  unit:'kg',  icon:'🍉', gst:0},
    {code:'F007',nm:'Papaya',     rate:40,  unit:'kg',  icon:'🍈', gst:0},
    {code:'F008',nm:'Pomegranate',rate:150, unit:'kg',  icon:'🍎', gst:0},
  ],
  'Coconut & Dry': [
    {code:'C001',nm:'Coconut',    rate:25,  unit:'pcs', icon:'🥥', gst:0},
    {code:'C002',nm:'Cashew',     rate:850, unit:'kg',  icon:'🥜', gst:5},
    {code:'C003',nm:'Almond',     rate:750, unit:'kg',  icon:'🥜', gst:5},
    {code:'C004',nm:'Peanut',     rate:120, unit:'kg',  icon:'🥜', gst:5},
  ],
  'Greens & Herbs': [
    {code:'G001',nm:'Mint',       rate:10,  unit:'bunch',icon:'🌿', gst:0},
    {code:'G002',nm:'Curry Leaves',rate:10, unit:'bunch',icon:'🍃', gst:0},
    {code:'G003',nm:'Ginger',     rate:120, unit:'kg',  icon:'🫚', gst:0},
    {code:'G004',nm:'Garlic',     rate:200, unit:'kg',  icon:'🧄', gst:0},
    {code:'G005',nm:'Green Chilli',rate:80, unit:'kg',  icon:'🌶', gst:0},
  ],
};
const ALL_VEG = Object.values(VEG_CATEGORIES).flat();
const VEG_CATS = ['All', ...Object.keys(VEG_CATEGORIES)];

const PAY_MODES = [{id:'cash',ic:'₹',lb:'Cash'},{id:'upi',ic:'📱',lb:'UPI'},{id:'card',ic:'💳',lb:'Card'}];

const UNIT_LABELS = { kg:'kg', bunch:'bunch', pcs:'pcs', dz:'dozen', gm:'gm' };

function fmt(n) { return (isNaN(n)?0:n).toLocaleString('en-IN',{minimumFractionDigits:2,maximumFractionDigits:2}); }
function nowStr() { return new Date().toLocaleTimeString('en-IN',{hour12:false}); }
function todayStr() { return new Date().toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}); }

export default function VegCounterPOSScreen({ theme, navigateTo }) {
  const [cat,       setCat]       = useState('All');
  const [searchQ,   setSearchQ]   = useState('');
  const [order,     setOrder]     = useState([]);
  const [selected,  setSelected]  = useState(null);   // item being weighed
  const [weightStr, setWeightStr] = useState('');     // weight input
  const [weightUnit,setWeightUnit]= useState('kg');
  const [payMode,   setPayMode]   = useState('cash');
  const [tendered,  setTendered]  = useState('');
  const [showPay,   setShowPay]   = useState(false);
  const [clock,     setClock]     = useState(nowStr());
  const [toast,     setToast]     = useState('');
  const [toastOn,   setToastOn]   = useState(false);
  const toastRef = useRef(null);
  const weightRef = useRef(null);

  useEffect(() => { const t = setInterval(() => setClock(nowStr()), 1000); return () => clearInterval(t); }, []);

  const showToast = useCallback((msg) => {
    setToast(msg); setToastOn(true);
    clearTimeout(toastRef.current);
    toastRef.current = setTimeout(() => setToastOn(false), 1800);
  }, []);

  useEffect(() => {
    const h = (e) => {
      if (e.key === 'F10') { e.preventDefault(); handleSave(); }
      if (e.key === 'F2')  { e.preventDefault(); voidLast(); }
      if (e.key === 'Escape') { setSelected(null); setWeightStr(''); }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  });

  useEffect(() => {
    if (selected && weightRef.current) weightRef.current.focus();
  }, [selected]);

  const tiles = (() => {
    const pool = cat === 'All' ? ALL_VEG : (VEG_CATEGORIES[cat] || []);
    if (!searchQ.trim()) return pool;
    const q = searchQ.toLowerCase();
    return ALL_VEG.filter(it => it.nm.toLowerCase().includes(q) || it.code.toLowerCase().includes(q));
  })();

  function selectItem(item) {
    setSelected(item);
    setWeightUnit(item.unit);
    setWeightStr('');
  }

  function numpadPress(val) {
    if (val === 'C') { setWeightStr(''); return; }
    if (val === '⌫') { setWeightStr(p => p.slice(0,-1)); return; }
    if (val === '.') { if (!weightStr.includes('.')) setWeightStr(p => p + '.'); return; }
    setWeightStr(p => p + val);
  }

  function confirmWeight() {
    if (!selected || !weightStr) return;
    const qty = parseFloat(weightStr);
    if (!qty || qty <= 0) { showToast('Enter valid weight'); return; }
    const lineAmt = selected.rate * qty;
    setOrder(prev => {
      const idx = prev.findIndex(r => r.code === selected.code && r.unit === weightUnit);
      if (idx >= 0) { const u=[...prev]; u[idx]={...u[idx],qty:u[idx].qty+qty,lineAmt:u[idx].lineAmt+lineAmt}; return u; }
      return [...prev, { ...selected, qty, unit: weightUnit, lineAmt }];
    });
    showToast(`${selected.nm} ${qty}${UNIT_LABELS[weightUnit]||weightUnit} added`);
    setSelected(null); setWeightStr('');
  }

  function voidLast() {
    if (!order.length) return;
    setOrder(p => p.slice(0,-1));
    showToast('Last item voided (F2)');
  }

  const totQty   = order.reduce((s,r) => s + r.qty, 0);
  const subTotal = order.reduce((s,r) => s + r.lineAmt, 0);
  const taxAmt   = order.reduce((s,r) => s + r.lineAmt * (r.gst||0)/100, 0);
  const grandTotal = Math.round(subTotal + taxAmt);
  const change     = Math.max(0, (+tendered||0) - grandTotal);

  function handleSave() {
    if (!order.length) { showToast('Add items before saving'); return; }
    showToast('Bill saved! ₹' + fmt(grandTotal));
    setShowPay(true);
  }

  function handleClear() {
    setOrder([]); setSelected(null); setWeightStr(''); setTendered(''); setShowPay(false);
    showToast('Bill cleared');
  }

  function confirmPayment() {
    if (+tendered < grandTotal) { showToast('Tendered amount is less'); return; }
    showToast('Payment confirmed! Change ₹' + fmt(change));
    setTimeout(handleClear, 1500);
  }

  const numpadKeys = ['7','8','9','4','5','6','1','2','3','C','0','.','⌫'];

  return (
    <div className="vpos-root" data-testid="veg-counter-pos-screen">
      <div className="vpos-main">

        {/* ── ORDER PANEL ─────────────────────────────────────────────── */}
        <div className="vpos-order">
          <div className="vpos-order-head">
            <span>Item</span><span>Qty/Wt</span><span>Rate</span><span>Amount</span>
          </div>
          <div className="vpos-order-list">
            {order.length === 0
              ? <div className="vpos-empty">Select item → enter weight → Add to Bill</div>
              : order.map((r, i) => (
                <div className="vpos-order-row" key={i}>
                  <span className="vpos-row-nm">{r.nm}</span>
                  <span className="vpos-mono vpos-row-qty">{r.qty.toFixed(r.unit==='pcs'||r.unit==='dz'?0:2)}{UNIT_LABELS[r.unit]||r.unit}</span>
                  <span className="vpos-mono vpos-row-rate">₹{r.rate}/{r.unit}</span>
                  <span className="vpos-mono vpos-row-val">
                    ₹{fmt(r.lineAmt)}
                    <button className="vpos-del" onClick={() => setOrder(p=>p.filter((_,j)=>j!==i))}>✕</button>
                  </span>
                </div>
              ))
            }
          </div>
          <div className="vpos-total-line">
            <span className="vpos-mono">{order.length} items</span>
            <span className="vpos-mono">₹{fmt(grandTotal)}</span>
          </div>

          <div className="vpos-order-actions">
            <div className="vpos-oa" onClick={() => showToast('Old bill…')}><div>📌</div><div>Old Bill</div></div>
            <div className="vpos-oa" onClick={voidLast}><div>🗑</div><div>Void (F2)</div></div>
            <div className="vpos-oa" onClick={() => showToast('Hold…')}><div>📁</div><div>Hold</div></div>
            <div className="vpos-oa" onClick={() => showToast('Get hold…')}><div>🗂</div><div>Get Hold</div></div>
          </div>

          <div className="vpos-info-grid">
            <div className="vpos-info"><label>Customer</label><span>Walk-in</span></div>
            <div className="vpos-info"><label>Items</label><span className="vpos-mono">{order.length}</span></div>
            <div className="vpos-info"><label>Subtotal</label><span className="vpos-mono">₹{fmt(subTotal)}</span></div>
            <div className="vpos-info"><label>Total Wt</label><span className="vpos-mono">{totQty.toFixed(2)} kg</span></div>
          </div>

          <div className="vpos-total-bar">
            <span>Total Amount</span>
            <span><span className="vpos-cur">INR</span><span className="vpos-mono" data-testid="veg-grand-total">₹{fmt(grandTotal)}</span></span>
          </div>
        </div>

        {/* ── WORKSPACE ───────────────────────────────────────────────── */}
        <div className="vpos-workspace">
          {showPay ? (
            /* PAYMENT VIEW */
            <div className="vpos-pay-view">
              <div className="vpos-pay-due">
                <span>Amount Due</span>
                <span className="vpos-mono" data-testid="veg-pay-due">₹{fmt(grandTotal)}</span>
              </div>
              <div className="vpos-pay-modes">
                {PAY_MODES.map(m => (
                  <div key={m.id} className={`vpos-pm${payMode===m.id?' active':''}`} onClick={() => setPayMode(m.id)}>
                    <div className="vpos-pm-ic">{m.ic}</div><div>{m.lb}</div>
                  </div>
                ))}
              </div>
              <div className="vpos-tender-row">
                <div className="vpos-tender-box">
                  <label>Tendered Amount</label>
                  <input type="number" className="vpos-mono" value={tendered} onChange={e=>setTendered(e.target.value)} placeholder="0.00" autoFocus data-testid="veg-tendered" />
                </div>
                <div className="vpos-tender-box">
                  <label>Change Due</label>
                  <input className="vpos-mono" value={fmt(change)} readOnly data-testid="veg-change" />
                </div>
              </div>
              <div className="vpos-quick-amts">
                {[100,200,500,1000].map(a=><button key={a} onClick={()=>setTendered(String(a))}>₹{a}</button>)}
                <button className="exact" onClick={()=>setTendered(String(grandTotal))}>Exact</button>
              </div>
              <button className="vpos-confirm" onClick={confirmPayment} data-testid="veg-confirm-pay">✓ Confirm Payment — ₹{fmt(grandTotal)}</button>
              <button className="vpos-back-pay" onClick={()=>setShowPay(false)}>← Back to Items</button>
            </div>
          ) : (
            <>
              {/* Item search */}
              <div className="vpos-search-bar">
                <input placeholder="Search item name or code…" value={searchQ} onChange={e=>setSearchQ(e.target.value)} data-testid="veg-search" />
              </div>
              {/* Category tabs */}
              <div className="vpos-cat-bar">
                {VEG_CATS.map(c => (
                  <button key={c} className={cat===c?'active':''} onClick={()=>{setCat(c);setSearchQ('');}} data-testid={`veg-cat-${c}`}>{c}</button>
                ))}
              </div>
              {/* Item tiles */}
              <div className="vpos-tile-grid" data-testid="veg-tile-grid">
                {tiles.map(it => (
                  <div key={it.code}
                    className={`vpos-tile${selected?.code===it.code?' sel':''}`}
                    onClick={() => selectItem(it)}
                    data-testid={`veg-tile-${it.code}`}>
                    <div className="vpos-tile-sw">{it.icon}</div>
                    <div className="vpos-tile-nm">{it.nm}</div>
                    <div className="vpos-tile-rate vpos-mono">₹{it.rate} <span className="vpos-tile-unit">/{it.unit}</span></div>
                  </div>
                ))}
                {tiles.length === 0 && <div className="vpos-tile-empty">No items</div>}
              </div>
            </>
          )}
        </div>

        {/* ── WEIGHT ENTRY / NUMPAD ─────────────────────────────────── */}
        <div className="vpos-weight-panel">
          {selected ? (
            <>
              <div className="vpos-wi-header">
                <div className="vpos-wi-icon">{selected.icon}</div>
                <div>
                  <div className="vpos-wi-name">{selected.nm}</div>
                  <div className="vpos-wi-rate vpos-mono">₹{selected.rate}/{selected.unit}</div>
                </div>
              </div>

              <div className="vpos-wi-unit-tabs">
                {(['kg','gm','pcs','bunch','dz']).map(u => (
                  <button key={u}
                    className={weightUnit===u?'active':''}
                    onClick={() => setWeightUnit(u)}
                    data-testid={`veg-unit-${u}`}>{UNIT_LABELS[u]||u}</button>
                ))}
              </div>

              <div className="vpos-wi-display">
                <div className="vpos-wi-val vpos-mono">{weightStr || '0'}</div>
                <div className="vpos-wi-unit-lbl">{UNIT_LABELS[weightUnit]||weightUnit}</div>
                {weightStr && (
                  <div className="vpos-wi-calc vpos-mono">
                    = ₹{fmt(selected.rate * (parseFloat(weightStr)||0))}
                  </div>
                )}
              </div>

              <div className="vpos-numpad">
                {numpadKeys.map(k => (
                  <button key={k}
                    className={`vpos-num-btn${k==='C'?' clear':''}${k==='⌫'?' del':''}`}
                    onClick={() => numpadPress(k)}
                    data-testid={`veg-num-${k}`}>{k}</button>
                ))}
              </div>

              <button className="vpos-wi-add" onClick={confirmWeight} data-testid="veg-add-btn">
                + Add to Bill
              </button>
              <button className="vpos-wi-cancel" onClick={() => { setSelected(null); setWeightStr(''); }}>
                Cancel
              </button>
            </>
          ) : (
            <div className="vpos-wi-placeholder">
              <div className="vpos-wi-ph-ic">⚖</div>
              <div className="vpos-wi-ph-txt">Select an item to enter weight / quantity</div>
            </div>
          )}
        </div>
      </div>

      {/* ── STATUS BAR ────────────────────────────────────────────────── */}
      <div className="vpos-status">
        <span>Organisation: <b>RetailCLIQ ERP</b></span>
        <span>ScreenId <b>6.16.13</b></span>
        <span>Caption: <b>Fresh / Veg Counter POS</b></span>
        <span>User: <b>SSADMIN</b> <span className="vpos-mono">{clock}</span></span>
        <span>Business Day: <b className="vpos-mono">{todayStr()}</b></span>
        <div style={{marginLeft:'auto',display:'flex',gap:'8px'}}>
          <div className="vpos-sb-ic">?</div>
          <div className="vpos-sb-ic">💬</div>
          <div className="vpos-sb-ic" onClick={handleSave} title="Save (F10)">💾</div>
        </div>
      </div>

      <div className={`vpos-toast${toastOn?' show':''}`}>{toast}</div>
    </div>
  );
}
